import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, DollarSign, Calendar, Users, 
  Loader2, RefreshCw, ArrowUp, ArrowDown
} from 'lucide-react';
import { Button } from './ui/button';
import { adminApi } from '../lib/api';

// Color palette
const COLORS = {
  primary: '#FFD700',
  secondary: '#1a1a1a',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  channels: {
    DIRECT: '#FFD700',
    PARKOS: '#3b82f6',
    PARKVIA: '#22c55e',
    ONEPARK: '#8b5cf6',
    LOOKING4PARKING: '#f97316',
    FREE2MOVE: '#ec4899'
  },
  status: {
    CONFIRMED: '#22c55e',
    COMPLETED: '#3b82f6',
    PENDING_PAYMENT: '#f59e0b',
    CANCELLED: '#ef4444',
    NO_SHOW: '#6b7280'
  }
};

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue }) => (
  <div className="bg-white/5 rounded-xl p-5 border border-white/10">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-zinc-400 text-sm">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-zinc-500 text-xs mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${trend === 'up' ? 'bg-green-500/20' : trend === 'down' ? 'bg-red-500/20' : 'bg-white/10'}`}>
        <Icon className={`w-5 h-5 ${trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : 'text-primary'}`} />
      </div>
    </div>
    {trendValue && (
      <div className={`flex items-center gap-1 mt-2 text-xs ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
        {trend === 'up' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        {trendValue}
      </div>
    )}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black/90 border border-white/20 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium">{label}</p>
        {payload.map((entry) => (
          <p key={`tooltip-${entry.name}-${entry.value}`} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes('€') ? `${(entry.value / 100).toFixed(2)}€` : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AdminAnalytics = () => {
  const { i18n } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, statsRes] = await Promise.all([
        adminApi.getAnalytics(period),
        adminApi.getStats()
      ]);
      setAnalytics(analyticsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Format daily data for chart
  const dailyChartData = analytics?.daily_reservations?.map(d => ({
    date: d._id?.substring(5) || '', // MM-DD format
    reservas: d.count,
    ingresos: d.revenue
  })) || [];

  // Format channel data for pie chart
  const channelChartData = analytics?.channels?.map(c => ({
    name: c._id || 'DIRECT',
    value: c.revenue,
    count: c.count
  })) || [];

  // Format status data
  const statusChartData = analytics?.status_distribution?.map(s => ({
    name: s._id,
    value: s.count
  })) || [];

  // Format payment data
  const paymentChartData = analytics?.payment_methods?.map(p => ({
    name: p._id === 'STRIPE' ? 'Tarjeta' : p._id === 'CASH' ? 'Efectivo' : p._id,
    value: p.count,
    revenue: p.revenue
  })) || [];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Analytics Dashboard</h2>
        <div className="flex items-center gap-3">
          <select 
            value={period} 
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
          </select>
          <Button onClick={fetchData} size="sm" className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Reservas" 
          value={stats?.total_reservations || 0}
          subtitle={`${stats?.today_reservations || 0} hoy`}
          icon={Calendar}
        />
        <StatCard 
          title="Ingresos Totales" 
          value={`${(stats?.total_revenue_euros || 0).toFixed(2)}€`}
          icon={DollarSign}
          trend="up"
        />
        <StatCard 
          title="Confirmadas" 
          value={stats?.confirmed || 0}
          subtitle="Activas"
          icon={Users}
        />
        <StatCard 
          title="Completadas" 
          value={stats?.completed || 0}
          icon={TrendingUp}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Reservations Chart */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4">Reservas e Ingresos Diarios</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyChartData}>
              <defs>
                <linearGradient id="colorReservas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="reservas" 
                stroke={COLORS.primary} 
                fillOpacity={1}
                fill="url(#colorReservas)"
                name="Reservas"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Channel */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4">Ingresos por Canal</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={channelChartData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {channelChartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS.channels[entry.name] || COLORS.primary} 
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => `${(value / 100).toFixed(2)}€`}
                contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Distribution */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4">Estado de Reservas</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={statusChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis type="number" stroke="#888" fontSize={12} />
              <YAxis dataKey="name" type="category" stroke="#888" fontSize={10} width={100} />
              <Tooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }} />
              <Bar dataKey="value" name="Reservas">
                {statusChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS.status[entry.name] || '#888'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Methods */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4">Métodos de Pago</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={paymentChartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
              >
                <Cell fill={COLORS.primary} />
                <Cell fill={COLORS.success} />
                <Cell fill={COLORS.warning} />
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Weekday Distribution */}
        <div className="bg-white/5 rounded-xl p-5 border border-white/10">
          <h3 className="text-white font-semibold mb-4">Reservas por Día</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={analytics?.weekday_distribution || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="day" stroke="#888" fontSize={12} />
              <YAxis stroke="#888" fontSize={12} />
              <Tooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }} />
              <Bar dataKey="count" fill={COLORS.primary} name="Reservas" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white/5 rounded-xl p-5 border border-white/10">
        <h3 className="text-white font-semibold mb-4">Actividad Reciente</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-zinc-400 text-sm py-2">Código</th>
                <th className="text-left text-zinc-400 text-sm py-2">Cliente</th>
                <th className="text-left text-zinc-400 text-sm py-2">Canal</th>
                <th className="text-left text-zinc-400 text-sm py-2">Estado</th>
                <th className="text-right text-zinc-400 text-sm py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.recent_activity?.map((r) => (
                <tr key={r.reservation_code} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 font-mono text-primary">{r.reservation_code}</td>
                  <td className="py-3 text-white">{r.customer_name}</td>
                  <td className="py-3">
                    <span 
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ 
                        backgroundColor: (COLORS.channels[r.source_channel] || COLORS.primary) + '20',
                        color: COLORS.channels[r.source_channel] || COLORS.primary
                      }}
                    >
                      {r.source_channel || 'DIRECT'}
                    </span>
                  </td>
                  <td className="py-3">
                    <span 
                      className="px-2 py-1 rounded text-xs"
                      style={{ 
                        backgroundColor: (COLORS.status[r.status] || '#888') + '20',
                        color: COLORS.status[r.status] || '#888'
                      }}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="py-3 text-right text-white font-medium">
                    {(r.total_cents / 100).toFixed(2)}€
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
